import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { Text, SessionCard, NoInformation, Progress } from '../../index';

import { alertError } from '../../../redux/stateActions';
import { GET_TODAY_SESSIONS, GET_UPCOMMING, GET_OTHER_SESSIONS } from '../../../services/provider';

import socket from '../../../socket';

const groupSession = (sessionArr) => {
    const newMeet = [];

    sessionArr.forEach((session, index) => {
        // if 1on1 session separate by meet
        if (session.meet.type === 'one') {
            if (!newMeet.some((meet) => meet.id === session.meet.id)) {
                // if session type is 1on1 use meet time
                if (session.meet.type === 'one') {
                    newMeet.push({
                        key: session.meet.id + index,
                        id: session.meet.id,
                        name: session.meet.name,
                        date: session.date,
                        startTime: session.startTime,
                        endTime: session.endTime,
                        patients: session.meet.patients,
                        isOnline: false,
                        type: session.meet.type,
                        staff: session.meet.staff,
                    });
                }
                // if session type is group use session time
                else {
                    newMeet.push({
                        key: session.meet.id + index,
                        id: session.meet.id,
                        name: session.meet.name,
                        date: session.date,
                        startTime: session.meet.startTime,
                        endTime: session.meet.endTime,
                        patients: session.meet.patients,
                        isOnline: false,
                        type: session.meet.type,
                        staff: session.meet.staff,
                    });
                }
            }
        }
        // if group session separate by session
        else {
            newMeet.push({
                key: session.meet.id + index,
                id: session.meet.id,
                name: session.meet.name,
                date: session.date,
                startTime: session.startTime,
                endTime: session.endTime,
                patients: session.meet.patients,
                isOnline: false,
                type: session.meet.type,
                staff: session.meet.staff,
                sessionId: session.id,
            });
        }
    });

    return newMeet;
};

const TodaySession = ({ selectedStaff }) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [today, setToday] = useState(null);
    const [endTime, setEndTime] = useState(null);

    const resetTime = () => {
        setToday(dayjs().format('YYYY-MM-DD'));
        setEndTime(dayjs().format('HH:mm:ss.SSS'));
    };

    useEffect(() => {
        resetTime();
        if (today) {
            getUpcomingSessionRefetch();
        }
    }, []);

    const variables = useMemo(() => {
        const newVariables = {
            hospital: user.staff.hospital.id,
            today: today,
            endTime: endTime,
        };

        if (selectedStaff !== '') {
            newVariables.staff = selectedStaff;
        }

        return newVariables;
    }, [selectedStaff, today, endTime]);

    /*--------------- Get Today session ---------------*/
    const [todayMeet, setTodayMeet] = useState([]);

    const {
        data: getTodaySessionData,
        loading: getTodaySessionLoading,
        error: getTodaySessionError,
        refetch: getTodaySessionrefetch,
    } = useQuery(GET_TODAY_SESSIONS, {
        variables: variables,
        skip: _.isEmpty(user) || !today || !endTime,
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getTodaySessionError) {
            dispatch(alertError(`Cannot get Today session: ${getTodaySessionError.message}`));
        }

        if (getTodaySessionData) {
            setTodayMeet(groupSession(getTodaySessionData.sessions));
        }
    }, [getTodaySessionError, getTodaySessionData]);

    /*--------------- Get Upcoming ---------------*/
    const [upcomingMeet, setUpcomingMeet] = useState([]);

    const {
        refetch: getUpcomingSessionRefetch,
        data: getUpcomingSessionData,
        loading: getUpcomingSessionLoading,
        error: getUpcomingSessionError,
    } = useQuery(GET_UPCOMMING, {
        variables: variables,
        skip: _.isEmpty(user) || !today,
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getUpcomingSessionError) {
            dispatch(alertError(`Cannot get Upcoming session: ${getUpcomingSessionError.message}`));
        }

        if (getUpcomingSessionData) {
            setUpcomingMeet(groupSession(getUpcomingSessionData.sessions));
        }
    }, [getUpcomingSessionError, getUpcomingSessionData]);

    /*--------------- Get Other Hospital Open Session ---------------*/
    const [otherMeets, setOtherMeets] = useState([]);

    const {
        data: getOtherMeetData,
        loading: getOtherMeetLoading,
        error: getOtherMeetError,
        refetch: getOtherMeetRefetch,
    } = useQuery(GET_OTHER_SESSIONS, {
        variables: {
            // hospital: user.staff.hospital.id,
            today: today,
            staffId: user.staff.id,
        },
        skip: _.isEmpty(user) || !today,
        pollInterval: 180000,
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getOtherMeetError) {
            dispatch(
                alertError(
                    `Cannot get other Counseling Service session: ${getOtherMeetError.message}`
                )
            );
        }

        if (getOtherMeetData) {
            setOtherMeets(
                groupSession(
                    getOtherMeetData.sessions.filter((session) =>
                        _.isEmpty(session.patient_summaries)
                    )
                )
            );
        }
    }, [getOtherMeetError, getOtherMeetData]);

    useEffect(() => {
        socket.on('newSession', (data) => {
            getOtherMeetRefetch();
        });
        socket.on('sessionUpdated', () => {
            getOtherMeetRefetch();
        });
        socket.on('calendarUpdated', () => {
            getTodaySessionrefetch();
            getUpcomingSessionRefetch();
        });
    }, []);

    const handleRedirect = (id, type) => {
        history.push({
            pathname: `/provider/session/${type}/${id}`,
            state: {
                prevPath: history.location.pathname,
                prevTab: 0,
            },
        });
    };

    const handleRedirectOtherOpen = (id) => {
        history.push({
            pathname: `/provider/session/other-open/${id}`,
            state: {
                prevPath: history.location.pathname,
                prevTab: 0,
            },
        });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', rowGap: '30px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '15px' }}>
                <Text $fontSize="16px" style={{ fontWeight: '500' }}>
                    Today’s session
                </Text>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: !_.isEmpty(todayMeet) ? 'repeat(3,1fr)' : '1fr',
                        columnGap: '20px',
                        rowGap: '20px',
                        position: 'relative',
                    }}
                >
                    {getTodaySessionLoading && <Progress />}
                    {!_.isEmpty(todayMeet) ? (
                        todayMeet.map((meet) => (
                            <SessionCard
                                data={meet}
                                key={meet.id}
                                onClick={() => handleRedirect(meet.id, meet.type)}
                                showProvider={selectedStaff === ''}
                                isMe={meet.staff?.id === user.staff?.id}
                            />
                        ))
                    ) : (
                        <NoInformation />
                    )}
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '15px' }}>
                <Text $fontSize="16px" style={{ fontWeight: '500' }}>
                    Upcoming sessions
                </Text>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: !_.isEmpty(upcomingMeet) ? 'repeat(3,1fr)' : '1fr',
                        columnGap: '20px',
                        rowGap: '20px',
                        position: 'relative',
                    }}
                >
                    {getUpcomingSessionLoading && <Progress />}
                    {!_.isEmpty(upcomingMeet) ? (
                        upcomingMeet.map((meet) => (
                            <SessionCard
                                data={meet}
                                key={meet.key}
                                onClick={() => handleRedirect(meet.id, meet.type)}
                                showProvider={selectedStaff === ''}
                                isMe={meet.staff?.id === user.staff?.id}
                            />
                        ))
                    ) : (
                        <NoInformation />
                    )}
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', rowGap: '15px' }}>
                <Text $fontSize="16px" style={{ fontWeight: '500' }}>
                    Other sessions
                </Text>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: !_.isEmpty(otherMeets) ? 'repeat(3,1fr)' : '1fr',
                        columnGap: '20px',
                        rowGap: '20px',
                        position: 'relative',
                    }}
                >
                    {getOtherMeetLoading && <Progress />}
                    {!_.isEmpty(otherMeets) ? (
                        otherMeets.map((meet) => (
                            <SessionCard
                                data={meet}
                                key={meet.key}
                                onClick={() => handleRedirectOtherOpen(meet.sessionId)}
                                showProvider
                            />
                        ))
                    ) : (
                        <NoInformation />
                    )}
                </div>
            </div>
        </div>
    );
};

TodaySession.propTypes = {
    selectedStaff: PropTypes.string.isRequired,
};

export default TodaySession;
